import { type QueryObserverOptions, useQuery } from '@tanstack/react-query';

import type {
  DtoV2ClassifyDocumentsClassifyDocumentsDetails,
  DtoV2ClassifyDocumentsDocumentTypesAndCategoryV2,
} from '~/__generated__/api';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { createPdfBlob } from '~/common/util/pdfUtils';

export const classifyKeys = {
  all: 'classify',
  quickMatch: 'quickMatch',
  adrCompletionStatus: 'adrCompletionStatus',
  documentPdf: 'documentPdf',
  documentAssociations: 'documentAssociations',
  documentTypeAssociations: 'documentTypeAssociations',
  classifyDocuments: 'classifyDocuments',
};

export function quickMatchDocumentsQueryOptions(
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
) {
  return {
    queryKey: [
      classifyKeys.all,
      classifyKeys.quickMatch,
      obfuscatedLoanIdentifier,
    ],
    enabled: !!obfuscatedLoanIdentifier,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.classifyDocumentsGetClassifiedDocumentsForBatchProcessing(
          {
            obfuscatedLoanIdentifier,
          },
        );

      return data;
    },
  } satisfies QueryObserverOptions;
}

export function useQuickMatchDocumentsQuery() {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery(quickMatchDocumentsQueryOptions(obfuscatedLoanIdentifier));
}

function getDocumentTypeAssociationsQueryOptions(
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
) {
  return {
    queryKey: [
      classifyKeys.all,
      classifyKeys.documentTypeAssociations,
      obfuscatedLoanIdentifier,
    ],
    staleTime: 0,
    enabled: !!obfuscatedLoanIdentifier,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v2.classifyDocumentsGetDocumentTypeAssociations({
          obfuscatedLoanIdentifier,
        });

      return data.documentTypesAndCategories;
    },
  } satisfies QueryObserverOptions;
}

export function useDocumentTypeAssociationsQuery<
  TSelected = DtoV2ClassifyDocumentsDocumentTypesAndCategoryV2[],
>({
  select,
}: {
  select?: (
    data: DtoV2ClassifyDocumentsDocumentTypesAndCategoryV2[],
  ) => TSelected;
} = {}) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    ...getDocumentTypeAssociationsQueryOptions(obfuscatedLoanIdentifier),
    select,
  });
}

export function useDocumentPdfQuery(documentId: number | undefined) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: [classifyKeys.all, classifyKeys.documentPdf, documentId],
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.classifyDocumentsGetDocumentPdfDetail(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
          documentId,
          {
            format: 'arraybuffer',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/pdf',
            },
          },
        );

      return await createPdfBlob(data as any as ArrayBuffer);
    },
  });
}

function documentAssociationsQueryOptions(
  obfuscatedLoanIdentifier: ObfuscatedLoanIdentifier,
  documentId: number | undefined,
  documentTypeId: number | string | undefined,
) {
  return {
    queryKey: [
      classifyKeys.all,
      classifyKeys.documentAssociations,
      { documentId, documentTypeId: Number(documentTypeId) },
      obfuscatedLoanIdentifier,
    ],
    staleTime: 5 * 60 * 1000,
    enabled: !!documentId && !!documentTypeId,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v2.documentAssociationGetDocumentAssociations({
          obfuscatedLoanIdentifier,
          documentId,
          documentTypeId: Number(documentTypeId),
        });

      return data;
    },
  } satisfies QueryObserverOptions;
}

export function useDocumentAssociationsQuery(
  documentId: number | undefined,
  documentTypeId: number | string | undefined,
) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery(
    documentAssociationsQueryOptions(
      obfuscatedLoanIdentifier,
      documentId,
      documentTypeId,
    ),
  );
}

export function useClassifyDocumentsQuery<
  TSelected = DtoV2ClassifyDocumentsClassifyDocumentsDetails[],
>({
  select,
}: {
  select?: (
    data: DtoV2ClassifyDocumentsClassifyDocumentsDetails[],
  ) => TSelected;
} = {}) {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: [
      classifyKeys.all,
      classifyKeys.classifyDocuments,
      obfuscatedLoanIdentifier,
    ],
    staleTime: 5 * 60 * 1000,
    enabled: !!obfuscatedLoanIdentifier,
    queryFn: async () => {
      const { data } =
        await typedApiClient.v2.classifyDocumentsGetClassifiedDocuments({
          obfuscatedLoanIdentifier,
        });
      return data.documentItems;
    },
    select,
  });
}
